/**
 * @file templateService.js
 * @description APIs for Template related requests.
 **/

import { contentHttp } from "./httpService";

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem("tenantId");


/**
 * Fetches the list of templates.
 *
 * @returns {Promise<Array>} A promise that resolves to the list of templates.
 */
export const fetchTemplates = async (tenantId) => {
  try {
    if(!tenantId) tenantId = getTenantId();
    const response = await contentHttp.get(
      `/v2/content/tenant/${tenantId}/templates`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }
};

/**
 * Fetches a population group by ID.
 *
 * @param {number} templateId The ID of the template.
 * @returns {Promise<Object>} A promise that resolves to the template data.
 */
export const fetchTemplateByID = async (templateId) => {
  if (!templateId) return;
  try {
    const tenantId = getTenantId();
    const response = await contentHttp.get(
      `/v2/content/tenant/${tenantId}/templates/single/${templateId}`
    );
    return response.data.viewModels[0];
  } catch (error) {
    console.error(`Error fetching template with ID ${templateId}:`, error);
    throw error;
  }
};

/**
 * Updates a template.
 *
 * @param {Object} updatedTemplate The updated data for the template.
 * @returns {Promise<Object>} A promise that resolves to the updated template data.
 */
export const updateTemplate = async (updatedTemplate) => {
  try {
    const tenantId = getTenantId();
    const response = await contentHttp.put(
      `/v2/content/tenant/${tenantId}/templates`,
      updatedTemplate
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error updating templates with ID ${updatedTemplate.id}:`,
      error
    );
    throw error;
  }
};

/**
 * Deletes a template by ID.
 *
 * @param {number} templateId The IDs of the groups to be deleted.
 * @returns {Promise<Object>} A promise that resolves to the deletion status.
 */
export const deleteTemplate = async (templateId) => {
  try {
    const tenantId = getTenantId();
    const response = await contentHttp.delete(
      `/v2/content/tenant/${tenantId}/templates/${templateId}?isTemplate=true`
    );
    return response.data;

  } catch (error) {
    console.error(
      `Error deleting template with ID ${templateId}:`,
      error
    );
    throw error;
  }
};

/**
 * Creates a template.
 *
 * @param {Object} newTemplate The new data for the template.
 * @returns {Promise<Object>} A promise that resolves to the new template data.
 */
export const createTemplate = async (newTemplate) => {
  try {
    const tenantId = getTenantId();
    const response = await contentHttp.post(
      `/v2/content/tenant/${tenantId}/templates`,
      newTemplate
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error creating template ${newTemplate.name}:`,
      error
    );
    throw error;
  }
};
